import React, { useEffect, useState } from "react";
import Select from "react-select";
import { generatePDFAndDownload, ListOfPdfs, getPreappById } from "../../../../../api";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import Modal from "../../../../components/modal";
import styles from "./styles.module.scss";
import toast from "react-hot-toast";
import useUserStore from "../../../../stores/user";
import useApplicationStore from "../../../../stores/application";

function GenerateDownload({ open, onClose }) {
  const activeApp = useApplicationStore((state) => state.activeApp);
  const loggedInUser = useUserStore((state) => state.user);
  const [activeBankProcessors, setActiveBankProcessors] = useState([]);
  const [show_spinner, setShowSpinner] = useState(false);
  const [list_of_pdfs, setListOfPdfs] = useState({});
  const [files_options, setFileOptions] = useState([]);
  const [isLoadingProcessors, setIsLoadingProcessors] = useState(true);

  const [selectedBank, setSelectedBank] = useState({});
  const [selectedFiles, setSelectedFiles] = useState();
  const [errors, setErrors] = useState([]);

  const onChangeTypeofBanks = (value) => {
    if (!value || !value.value) {
      setSelectedBank({});
      setFileOptions([]);
      setSelectedFiles(null);
      return;
    }

    setSelectedBank(value);
    
    const processorName = value.label || value.value;
    
    // Add null checks
    const processor_files = list_of_pdfs[processorName];
    if (!processor_files) {
      console.warn(`No files found for processor: ${processorName}`);
      setFileOptions([]);
      setSelectedFiles(null);
      return;
    }

    // Add null checks for application and addendums arrays
    const applications = (processor_files.application || []).map((app) => ({
      value: app,
      label: app,
    }));
    
    const addendums = (processor_files.addendums || []).map((addendum) => ({
      value: addendum,
      label: addendum,
    }));
    
    const file_ops = [
      {
        label: "Applications",
        options: applications,
      },
      {
        label: "Addendums",
        options: addendums,
      },
    ];
    
    setFileOptions(file_ops);
    setSelectedFiles(null); // Reset file selection when bank changes
  };

  const onChangeFileSelect = (value) => {
    setSelectedFiles(value);
  };

  const handleSubmit = (action_type) => {
    // Clear previous errors
    setErrors([]);
    
    if (!selectedBank.value) {
      setErrors(['Please select a processor']);
      return;
    }
    
    if (!selectedFiles || !selectedFiles.value) {
      setErrors(['Please select a document type']);
      return;
    }

    const toastId = toast.loading("Generating PDF...");
    setShowSpinner(true);
    
    generatePDFAndDownload({
      slug: activeApp.editslug,
      pdf_file_name: selectedFiles.value,
      bank: selectedBank.value,
      action_type: action_type,
    }).then((res) => {
      setShowSpinner(false);
      if (res?.status) {
        if (action_type === 'send_for_signature') {
          if (res?.pending_approval) {
            toast.error("Pending manager approval", { id: toastId });
          } 
          else if (res?.requires_approval) {
            toast.success("Sent to manager for approval", { id: toastId });
          }
          else {
            toast.success("Email sent for signature", { id: toastId });
          }
        } else {
          toast.success("PDF Generated Successfully", { id: toastId });
          window.open(res?.fileUrl, "_blank", "noreferrer");
        }
        onClose();
      } else {
        toast.error("Something went wrong", { id: toastId });
        if (res?.errors) {
          setErrors(Array.isArray(res?.errors) ? res?.errors : [res?.errors]);
        }
      }
    })
    .catch((err) => {
      setShowSpinner(false);
      toast.error("Something went wrong", { id: toastId });
      if (err?.data && err?.data?.errors) {
        setErrors(Array.isArray(err?.data?.errors) ? err?.data?.errors : [err?.data?.errors]);
      } else {
        setErrors(['An unexpected error occurred']);
      }
    });
  };

  useEffect(() => {
    ListOfPdfs().then((res) => {
      setListOfPdfs(res?.data?.bank_file_mapping);
      
      getPreappById({
        id: activeApp.id
      }).then(response => {
        let processor_options = [];
        if (response?.data?.organization?.bank_applications) {          
          // Organization has specific banks set          
          processor_options = response?.data?.organization?.bank_applications?.map(bank => {
            return {
              label: bank.label,
              value: bank.label
            }
          });
        } else {
          // No banks set for the organization, show all processors
          processor_options = Object.entries(res?.data?.bank_file_mapping).map(
            ([key, val]) => {
              return {
                label: key,
                value: key,
              };
            },
          );
        }

        if (loggedInUser?.is_ems_user) {
          processor_options = processor_options.filter((item) =>
            item.label.includes("EMS") || item.label.includes("Kurv")
          );
        }
        setActiveBankProcessors(processor_options);
        setIsLoadingProcessors(false);
      }).catch((error) => {
        console.error("Error loading processors:", error);
        setIsLoadingProcessors(false);
      });
    }).catch((error) => {
      console.error("Error loading PDFs:", error);
      setIsLoadingProcessors(false);
    });
  }, []);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      header={""}
      size="sm"
    >
      <div className={styles.modalBody}>

        <h6 className={styles.heading} >Generate and Download</h6>

        <div className={styles.fieldGroup}>
          <div>
            <label>Processor</label>
            {isLoadingProcessors ? (
              <div className={styles.loadingText}>Loading processors...</div>
            ) : activeBankProcessors.length ? (
              <Select
                name="colors"
                isLoading={show_spinner}
                isDisabled={show_spinner}
                options={activeBankProcessors}
                onChange={onChangeTypeofBanks}
                className={styles.selectInput}
                classNames={{
                  control: () => styles.selectControl, // Map the CSS Module class
                }}
                classNamePrefix="select"
              />
            ) : (
              <div className={styles.noProcessorsText}>No processors available</div>
            )}
          </div>

          <div>
            {Object.keys(selectedBank).length > 0 ? (
              <>
                <br />
                <label>Document Type</label>
                <Select
                  name="files"
                  isLoading={show_spinner}
                  isDisabled={show_spinner}
                  options={files_options}
                  value={selectedFiles}
                  onChange={onChangeFileSelect}
                  className={styles.selectInput}
                  classNames={{
                    control: () => styles.selectControl, // Map the CSS Module class
                  }}
                  classNamePrefix="select"
                />
              </>
            ) : (
              <></>
            )}
          </div>
        </div>

        {/* Error Display */}
        {Array.isArray(errors) && errors.length > 0 && (
          <div className={styles.errorSection}>
            {errors.map((error, index) => (
              <p key={index} className={styles.errorText}>{error}</p>
            ))}
          </div>
        )}
      </div>

      <div className={styles.modalFooter}>
        <ButtonIcon textColor="#254FE2" onClick={onClose}>
          Cancel
        </ButtonIcon>
        {selectedBank?.value ? (
          <ButtonIcon
            bgColor={"#4C70EF"}
            textColor="#FFF"
            disabled={show_spinner}
            onClick={() => handleSubmit('send_for_signature')}
          >
            Send For Signature
          </ButtonIcon>
        ) : null}
        {selectedBank?.value ? (
          <ButtonIcon
            bgColor={"#4C70EF"}
            textColor="#FFF"
            disabled={show_spinner}
            onClick={() => handleSubmit('download')}
          >
            Download
          </ButtonIcon>
        ) : null}
      </div>
    </Modal>
  );
}

export default GenerateDownload;
